// src/components/admin/dsat/AccessControl.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../../firebaseConfig';
import { backdropDismiss } from '../../../utils/backdropDismiss';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  FiCheckCircle,
  FiChevronDown,
  FiLock,
  FiSearch,
  FiSettings,
  FiUnlock,
  FiUser,
  FiX,
} from 'react-icons/fi';
import './AccessControl.css';

// Display order: SAT → PSAT → ACT
const TEST_FAMILIES = [
  { key: 'SAT',  label: 'SAT',  collectionName: 'sat_diagnostic_tests'  },
  { key: 'PSAT', label: 'PSAT', collectionName: 'psat_diagnostic_tests' },
  { key: 'ACT',  label: 'ACT',  collectionName: 'act_diagnostic_tests'  },
];

// Score ceilings used in the "score / max" display
const SCORE_MAX = { SAT: 1600, PSAT: 1520, ACT: 36 };

const EXTENDED_TIME_OPTIONS = [
  { label: 'Default (Standard Time)', value: 0   },
  { label: '+25%',                    value: 25  },
  { label: '+50%',                    value: 50  },
  { label: '+75%',                    value: 75  },
  { label: '+100%',                   value: 100 },
];

function getStudentLabel(student) {
  return (
    student?.name        ||
    student?.fullName    ||
    student?.displayName ||
    student?.email       ||
    'Unnamed Student'
  );
}

function getTestLabel(test) {
  return test?.name || test?.title || test?.testName || test?.label || test?.id;
}

/** Returns epoch ms from a Firestore Timestamp, Date, or number. */
function toMs(v) {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  return 0;
}

// ── PSAT scaling helpers ──────────────────────────────────────────────────────

/**
 * Given a PSAT attempt, determine which scoring table column key to use.
 * subject: 'rw' | 'math'
 * Returns e.g. 'rwModuleB', 'mathModuleA', etc.
 */
function psatModule2Key(attempt, subject) {
  if (subject === 'rw') {
    const m = attempt.englishModule2;
    if (m) return m.includes('_B') ? 'rwModuleB' : 'rwModuleA';
    // pathHistory fallback (older results)
    const hist = Array.isArray(attempt.pathHistory) ? attempt.pathHistory : [];
    const mod2 = hist.find(
      (h) => h.module === 'English_Module_A' || h.module === 'English_Module_B'
    );
    return mod2?.module?.includes('_B') ? 'rwModuleB' : 'rwModuleA';
  }
  // math
  const m = attempt.mathModule2;
  if (m) return m.includes('_B') ? 'mathModuleB' : 'mathModuleA';
  const hist = Array.isArray(attempt.pathHistory) ? attempt.pathHistory : [];
  const mod2 = hist.find(
    (h) => h.module === 'Math_Module_A' || h.module === 'Math_Module_B'
  );
  return mod2?.module?.includes('_B') ? 'mathModuleB' : 'mathModuleA';
}

/**
 * Look up a raw score in a PSAT scoring table column.
 * table is the Firestore document data (keys = column names, values = { "0": score, "1": score, … })
 */
function psatLookupScale(table, colKey, rawScore) {
  if (!table || rawScore == null) return null;
  const col = table[colKey];
  if (!col) return null;
  const val = col[String(rawScore)];
  if (val == null || val === 'N/A') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

/**
 * Compute total PSAT scaled score for one attempt, given the scoring table doc.
 * Returns null if the table is missing or look-ups fail.
 */
function computePsatScaled(attempt, table) {
  if (!table) return null;
  const rwRaw   = attempt.englishRawScore ?? null;
  const mathRaw = attempt.mathRawScore    ?? null;
  const rwScaled   = psatLookupScale(table, psatModule2Key(attempt, 'rw'),   rwRaw);
  const mathScaled = psatLookupScale(table, psatModule2Key(attempt, 'math'), mathRaw);
  if (rwScaled == null || mathScaled == null) return null;
  return rwScaled + mathScaled;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AccessControl() {
  const location = useLocation();
  const navigate = useNavigate();
  const [students,          setStudents]          = useState([]);
  const [testsByFamily,     setTestsByFamily]     = useState([]);
  const [selectedStudent,   setSelectedStudent]   = useState(null);
  const [accessMap,         setAccessMap]         = useState({});
  // scoresMap[testId] = { score: number, max: number } | undefined
  const [scoresMap,         setScoresMap]         = useState({});
  // All families start collapsed for a compact initial view
  const [collapsedFamilies, setCollapsedFamilies] = useState(() => {
    const init = {};
    TEST_FAMILIES.forEach((f) => { init[f.key] = true; });
    return init;
  });
  const [studentSearch,     setStudentSearch]     = useState('');
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [settingsModal,     setSettingsModal]     = useState(null);
  const [settingsDraftPercent, setSettingsDraftPercent] = useState(0);
  const [savingSettings,    setSavingSettings]    = useState(false);
  const routeParams = new URLSearchParams(location.search);
  const routeStudentId = routeParams.get("student") || "";
  const routeSearch = routeParams.get("q") || "";

  const studentDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(e.target)) {
        setStudentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch students — sorted by createdAt desc (newest first), then alphabetically
  useEffect(() => {
    const run = async () => {
      const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'student')));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const diff = toMs(b.createdAt) - toMs(a.createdAt);
        return diff !== 0 ? diff : getStudentLabel(a).localeCompare(getStudentLabel(b));
      });
      setStudents(items);
    };
    run();
  }, []);

  useEffect(() => {
    if (studentSearch !== routeSearch) {
      setStudentSearch(routeSearch);
    }

    if (!routeStudentId) {
      if (selectedStudent) setSelectedStudent(null);
      return;
    }

    const matchedStudent = students.find((student) => student.id === routeStudentId) || null;
    if (matchedStudent && selectedStudent?.id !== matchedStudent.id) {
      setSelectedStudent(matchedStudent);
    } else if (!matchedStudent && selectedStudent?.id === routeStudentId) {
      setSelectedStudent(null);
    }
  }, [routeSearch, routeStudentId, selectedStudent, studentSearch, students]);

  // Fetch all tests grouped by family
  useEffect(() => {
    const run = async () => {
      const results = await Promise.all(
        TEST_FAMILIES.map(async (family) => {
          const snap = await getDocs(collection(db, family.collectionName));
          const tests = snap.docs
            .map((d) => ({ id: d.id, ...d.data(), familyKey: family.key, familyLabel: family.label }))
            .sort((a, b) => getTestLabel(a).localeCompare(getTestLabel(b)));
          return { key: family.key, label: family.label, tests };
        })
      );
      setTestsByFamily(results.filter((f) => f.tests.length > 0));
    };
    run();
  }, []);

  // Real-time access map for selected student
  useEffect(() => {
    if (!selectedStudent) { setAccessMap({}); return; }
    const q = query(
      collection(db, 'student_test_access'),
      where('studentId', '==', selectedStudent.id)
    );
    const unsub = onSnapshot(q, (snap) => {
      const next = {};
      snap.forEach((d) => {
        const data = d.data();
        next[data.testId] = {
          unlocked:            data.unlocked === true,
          extendedTimePercent: typeof data.extendedTimePercent === 'number' ? data.extendedTimePercent : 0,
          finished:            data.finished === true,
          finishedAt:          data.finishedAt || null,
        };
      });
      setAccessMap(next);
    });
    return () => unsub();
  }, [selectedStudent]);

  // Fetch best scores from diagnostic_results with proper scaling per test type
  useEffect(() => {
    if (!selectedStudent) { setScoresMap({}); return; }

    const run = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'diagnostic_results'), where('studentId', '==', selectedStudent.id))
        );

        // Bucket results by type for separate handling
        const satAttempts  = [];
        const actAttempts  = [];
        const psatAttempts = [];

        snap.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          if      (data.type === 'ACT')  actAttempts.push(data);
          else if (data.type === 'PSAT') psatAttempts.push(data);
          else                           satAttempts.push(data);
        });

        // Fetch PSAT scoring tables for all unique PSAT testIds in parallel
        const psatTestIds = [...new Set(psatAttempts.map((r) => r.testId).filter(Boolean))];
        const psatTables  = {};
        await Promise.all(
          psatTestIds.map(async (tid) => {
            try {
              const tableSnap = await getDoc(
                doc(db, 'psat_diagnostic_tests', tid, 'scoring', 'psat_score_table')
              );
              if (tableSnap.exists()) psatTables[tid] = tableSnap.data();
            } catch {
              // table not uploaded yet — skip
            }
          })
        );

        // best[testId] = { score: number, max: number }
        const best = {};

        const updateBest = (testId, score, max) => {
          if (testId == null || score == null) return;
          if (best[testId] == null || score > best[testId].score) {
            best[testId] = { score, max };
          }
        };

        // SAT — scaled total score
        satAttempts.forEach((data) => {
          updateBest(data.testId, data.totalScore ?? null, SCORE_MAX.SAT);
        });

        // ACT — composite score
        actAttempts.forEach((data) => {
          const composite = data.composite ?? data.compositeScore ?? data.actComposite ?? null;
          updateBest(data.testId, composite, SCORE_MAX.ACT);
        });

        // PSAT — prefer computed scaled total; fall back to raw total if table not available
        psatAttempts.forEach((data) => {
          const table  = psatTables[data.testId] || null;
          const scaled = computePsatScaled(data, table);
          // If scoring table exists and scaling succeeded, use scaled. Otherwise omit score
          // (showing raw score as a PSAT "total" is misleading, so we only show if scaled).
          if (scaled != null) {
            updateBest(data.testId, scaled, SCORE_MAX.PSAT);
          }
        });

        setScoresMap(best);
      } catch (err) {
        console.warn('AccessControl: could not load scores', err);
      }
    };

    run();
  }, [selectedStudent]);

  const filteredStudents = students.filter((s) => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      getStudentLabel(s).toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  });

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setStudentSearch(getStudentLabel(student));
    setStudentDropdownOpen(false);
    setSettingsModal(null);
    const params = new URLSearchParams(location.search);
    params.set("student", student.id);
    params.set("q", getStudentLabel(student));
    const nextSearch = params.toString();
    const nextUrl = nextSearch ? `/diagnostics/access?${nextSearch}` : "/diagnostics/access";
    if (`${location.pathname}${location.search}` !== nextUrl) {
      navigate(nextUrl);
    }
  };

  const toggleFamily = (key) =>
    setCollapsedFamilies((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleAccess = async (test) => {
    if (!selectedStudent) return;
    const cur = accessMap[test.id] || { unlocked: false, extendedTimePercent: 0, finished: false };
    if (cur.finished) return;
    const docRef = doc(db, 'student_test_access', `${selectedStudent.id}_${test.id}`);
    await setDoc(
      docRef,
      {
        studentId:           selectedStudent.id,
        testId:              test.id,
        testFamily:          test.familyKey,
        unlocked:            !cur.unlocked,
        extendedTimePercent: cur.extendedTimePercent ?? 0,
      },
      { merge: true }
    );
    // If we just locked the test, close any open settings modal for it
    if (cur.unlocked && settingsModal?.testId === test.id) setSettingsModal(null);
  };

  const openSettingsModal = (test) => {
    const cur = accessMap[test.id] || { unlocked: false, extendedTimePercent: 0, finished: false };
    if (!cur.unlocked || cur.finished) return;
    setSettingsModal({ testId: test.id, testLabel: getTestLabel(test), familyLabel: test.familyLabel });
    setSettingsDraftPercent(cur.extendedTimePercent ?? 0);
  };

  const applyExtendedTime = async () => {
    if (!selectedStudent || !settingsModal) return;
    const cur = accessMap[settingsModal.testId] || { unlocked: true, extendedTimePercent: 0, finished: false };
    if (cur.finished) { setSettingsModal(null); return; }
    setSavingSettings(true);
    try {
      const docRef = doc(db, 'student_test_access', `${selectedStudent.id}_${settingsModal.testId}`);
      await setDoc(
        docRef,
        {
          studentId:           selectedStudent.id,
          testId:              settingsModal.testId,
          unlocked:            cur.unlocked === true,
          extendedTimePercent: Number(settingsDraftPercent) || 0,
        },
        { merge: true }
      );
      setSettingsModal(null);
    } finally {
      setSavingSettings(false);
    }
  };

  // Aggregate stats
  const totalTests    = testsByFamily.reduce((s, f) => s + f.tests.length, 0);
  const totalUnlocked = testsByFamily.reduce(
    (s, f) => s + f.tests.filter((t) => accessMap[t.id]?.unlocked && !accessMap[t.id]?.finished).length, 0
  );
  const totalFinished = testsByFamily.reduce(
    (s, f) => s + f.tests.filter((t) => accessMap[t.id]?.finished).length, 0
  );

  const selectedStudentLabel = selectedStudent ? getStudentLabel(selectedStudent) : '';

  return (
    <div className="access-control">
      {/* ── Header ── */}
      <div className="access-control__header">
        <h2>Student Test Access</h2>
        <p>Manage diagnostic test access and accommodations for each student.</p>
      </div>

      {/* ── Body ── */}
      <div className="access-control__body">

        {/* Student search / picker */}
        <div className="student-picker" ref={studentDropdownRef}>
          <div className="student-picker__input-wrap">
            <FiSearch className="student-picker__icon" />
            <input
              type="text"
              autoComplete="off"
              className="student-picker__input"
              placeholder="Search student by name or email…"
              value={studentSearch}
              onFocus={() => setStudentDropdownOpen(true)}
              onChange={(e) => {
                const nextValue = e.target.value;
                setStudentSearch(nextValue);
                setStudentDropdownOpen(true);
                const params = new URLSearchParams(location.search);
                if (nextValue) params.set("q", nextValue);
                else params.delete("q");
                const nextSearch = params.toString();
                const nextUrl = nextSearch ? `/diagnostics/access?${nextSearch}` : "/diagnostics/access";
                if (`${location.pathname}${location.search}` !== nextUrl) {
                  navigate(nextUrl);
                }
              }}
            />
            {studentSearch ? (
              <button
                type="button"
                className="student-picker__action-btn"
                onClick={() => {
                  setStudentSearch('');
                  setStudentDropdownOpen(true);
                  const params = new URLSearchParams(location.search);
                  params.delete("q");
                  const nextSearch = params.toString();
                  const nextUrl = nextSearch ? `/diagnostics/access?${nextSearch}` : "/diagnostics/access";
                  if (`${location.pathname}${location.search}` !== nextUrl) {
                    navigate(nextUrl);
                  }
                }}
                aria-label="Clear search"
              >
                <FiX />
              </button>
            ) : (
              <button
                type="button"
                className="student-picker__action-btn"
                onClick={() => setStudentDropdownOpen((v) => !v)}
                aria-label="Toggle student list"
              >
                <FiChevronDown />
              </button>
            )}
          </div>

          {studentDropdownOpen && (
            <div className="student-picker__dropdown">
              {filteredStudents.length === 0 ? (
                <div className="student-picker__empty">No students found.</div>
              ) : (
                filteredStudents.map((student) => (
                  <button
                    type="button"
                    key={student.id}
                    className={`student-picker__option ${selectedStudent?.id === student.id ? 'is-active' : ''}`}
                    onClick={() => handleSelectStudent(student)}
                  >
                    <span className="student-picker__option-name">{getStudentLabel(student)}</span>
                    {student.email && (
                      <span className="student-picker__option-meta">{student.email}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Empty state */}
        {!selectedStudent ? (
          <div className="access-control__empty-state">
            <div className="empty-icon-wrap"><FiUser /></div>
            <div>
              <div className="empty-title">No student selected</div>
              <div className="empty-sub">Search for a student above to manage their SAT, PSAT, and ACT test access.</div>
            </div>
          </div>
        ) : (
          <div className="access-control__student-view">

            {/* ── Student Summary Card ── */}
            <div className="student-card">
              <div className="student-card__avatar">
                {selectedStudentLabel.charAt(0).toUpperCase()}
              </div>
              <div className="student-card__info">
                <div className="student-card__name">{selectedStudentLabel}</div>
                {selectedStudent.email && (
                  <div className="student-card__email">{selectedStudent.email}</div>
                )}
              </div>
              <div className="student-card__stats">
                <div className="stat-chip">
                  <span className="stat-chip__val">{totalTests}</span>
                  <span className="stat-chip__lbl">Tests</span>
                </div>
                <div className="stat-chip stat-chip--blue">
                  <span className="stat-chip__val">{totalUnlocked}</span>
                  <span className="stat-chip__lbl">Unlocked</span>
                </div>
                <div className="stat-chip stat-chip--green">
                  <span className="stat-chip__val">{totalFinished}</span>
                  <span className="stat-chip__lbl">Done</span>
                </div>
              </div>
            </div>

            {/* ── Test Families ── */}
            <div className="test-families">
              {testsByFamily.map((family) => {
                const isCollapsed    = collapsedFamilies[family.key] === true;
                const familyUnlocked = family.tests.filter(
                  (t) => accessMap[t.id]?.unlocked && !accessMap[t.id]?.finished
                ).length;
                const familyFinished = family.tests.filter(
                  (t) => accessMap[t.id]?.finished
                ).length;

                return (
                  <section className="test-family" key={family.key}>
                    {/* Collapsible header */}
                    <button
                      type="button"
                      className="test-family__header"
                      onClick={() => toggleFamily(family.key)}
                      aria-expanded={!isCollapsed}
                    >
                      <div className="test-family__header-left">
                        <span className="test-family__label">{family.label}</span>
                        <div className="family-chips">
                          <span className="fchip fchip--gray">
                            {family.tests.length} test{family.tests.length !== 1 ? 's' : ''}
                          </span>
                          {familyUnlocked > 0 && (
                            <span className="fchip fchip--blue">{familyUnlocked} unlocked</span>
                          )}
                          {familyFinished > 0 && (
                            <span className="fchip fchip--green">{familyFinished} done</span>
                          )}
                        </div>
                      </div>
                      <FiChevronDown className={`family-chevron ${isCollapsed ? '' : 'is-open'}`} />
                    </button>

                    {/* Test list (only when expanded) */}
                    {!isCollapsed && (
                      <div className="test-family__list">
                        {family.tests.map((test) => {
                          const access     = accessMap[test.id] || { unlocked: false, extendedTimePercent: 0, finished: false };
                          const isUnlocked = access.unlocked === true;
                          const isFinished = access.finished === true;
                          const extPercent = access.extendedTimePercent ?? 0;
                          const scoreInfo  = scoresMap[test.id]; // { score, max } | undefined

                          return (
                            <div className="test-row" key={test.id}>
                              <div className="test-row__main">
                                <div className="test-row__name">{getTestLabel(test)}</div>
                                <div className="test-row__subtext">
                                  {isFinished ? (
                                    <span className="subtext--finished">
                                      Finished
                                      {scoreInfo != null && (
                                        <span className="score-badge">
                                          {' '}· {scoreInfo.score} / {scoreInfo.max}
                                        </span>
                                      )}
                                    </span>
                                  ) : isUnlocked ? (
                                    extPercent > 0
                                      ? <>Unlocked &middot; Extended Time +{extPercent}%</>
                                      : <>Unlocked &middot; Standard Time</>
                                  ) : (
                                    <>Locked</>
                                  )}
                                </div>
                              </div>

                              <div className="test-row__actions">
                                {isFinished ? (
                                  <div className="status-pill status-pill--finished">
                                    <FiCheckCircle /> Finished
                                  </div>
                                ) : (
                                  <>
                                    {isUnlocked && (
                                      <button
                                        type="button"
                                        className="icon-btn"
                                        onClick={() => openSettingsModal(test)}
                                        aria-label={`Settings for ${getTestLabel(test)}`}
                                        title="Extended Time Settings"
                                      >
                                        <FiSettings />
                                      </button>
                                    )}
                                    <label className="toggle" title={isUnlocked ? 'Unlocked' : 'Locked'}>
                                      <input
                                        type="checkbox"
                                        checked={isUnlocked}
                                        onChange={() => toggleAccess(test)}
                                      />
                                      <span className="toggle__track">
                                        <span className="toggle__thumb" />
                                      </span>
                                      <span className={`toggle__label ${isUnlocked ? 'is-on' : 'is-off'}`}>
                                        {isUnlocked ? <><FiUnlock /> Unlocked</> : <><FiLock /> Locked</>}
                                      </span>
                                    </label>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}

              {/* Bottom breathing room */}
              <div className="test-families__spacer" />
            </div>
          </div>
        )}
      </div>

      {/* ── Extended Time Modal ── */}
      {settingsModal && (
        <div
          className="modal-overlay"
          {...backdropDismiss(() => { if (!savingSettings) setSettingsModal(null); })}
          role="presentation"
        >
          <div
            className="settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="et-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="settings-modal__header">
              <div>
                <h4 id="et-modal-title">Extended Time Settings</h4>
                <p>{settingsModal.familyLabel} &middot; {settingsModal.testLabel}</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setSettingsModal(null)}
                disabled={savingSettings}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="settings-modal__body">
              <label htmlFor="et-select" className="settings-modal__label">Time Extension</label>
              <select
                id="et-select"
                className="settings-modal__select"
                value={String(settingsDraftPercent)}
                onChange={(e) => setSettingsDraftPercent(Number(e.target.value))}
                disabled={savingSettings}
              >
                {EXTENDED_TIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="settings-modal__footer">
              <button
                type="button"
                className="ac-btn ac-btn--ghost"
                onClick={() => setSettingsModal(null)}
                disabled={savingSettings}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ac-btn ac-btn--primary"
                onClick={applyExtendedTime}
                disabled={savingSettings}
              >
                {savingSettings ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
